
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import { IconButton, Stack, Typography } from "@mui/material";

import AppBottomSheet from "./AppBottomSheet";
import AppSearchField from "./AppSearchField";
import AppCard from "./AppCard";
import type { AllocationMasterRecord } from "../types/allocationMasterData";
import { formatAllocationNumber } from "../utils/allocationNumber";
import { APP_UI } from "../ui/appUi";

export default function AllocationSelectionSheet({
  open,
  query,
  results,
  total,
  page,
  pageSize,
  onQueryChange,
  onPageChange,
  onSelect,
  onClose,
}: {
  open: boolean;
  query: string;
  results: AllocationMasterRecord[];
  total: number;
  page: number;
  pageSize: number;
  onQueryChange: (query: string) => void;
  onPageChange: (page: number) => void;
  onSelect: (record: AllocationMasterRecord) => void;
  onClose: () => void;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);

  return (
    <AppBottomSheet open={open} onClose={onClose} title="Kontierung auswählen" subtitle={`${total.toLocaleString("de-DE")} Treffer`}>
      <AppSearchField value={query} onChange={onQueryChange} placeholder="Auftragsnummer, Anlage oder Anlagenbereich" autoFocus />

      <Stack direction="row" spacing={0.25} sx={{ alignItems: "center", minHeight: 38 }}>
        <Typography variant="caption" sx={{ flex: 1, color: APP_UI.textMuted }}>
          {first}–{last} Kontierungen
        </Typography>
        <IconButton aria-label="Erste Kontierungsseite" title="Erste Seite" size="small" disabled={page === 0} onClick={() => onPageChange(0)} sx={{ color: APP_UI.accent }}><FirstPageRoundedIcon /></IconButton>
        <IconButton aria-label="Vorherige Kontierungsseite" title="Vorherige Seite" size="small" disabled={page === 0} onClick={() => onPageChange(page - 1)} sx={{ color: APP_UI.accent }}><NavigateBeforeRoundedIcon /></IconButton>
        <Typography variant="caption" sx={{ minWidth: 44, textAlign: "center", color: "#c7c8cc", fontWeight: 800 }}>{page + 1}/{pageCount}</Typography>
        <IconButton aria-label="Nächste Kontierungsseite" title="Nächste Seite" size="small" disabled={page >= pageCount - 1} onClick={() => onPageChange(page + 1)} sx={{ color: APP_UI.accent }}><NavigateNextRoundedIcon /></IconButton>
        <IconButton aria-label="Letzte Kontierungsseite" title="Letzte Seite" size="small" disabled={page >= pageCount - 1} onClick={() => onPageChange(pageCount - 1)} sx={{ color: APP_UI.accent }}><LastPageRoundedIcon /></IconButton>
      </Stack>

      <Stack spacing={0.65}>
        {results.map((item) => (
          <AppCard key={item.entryKey} component="button" type="button" role="button" onClick={() => onSelect(item)} sx={{ width: "100%", textAlign: "left", color: "inherit", font: "inherit" }}>
            <Typography variant="subtitle2" sx={{ color: APP_UI.accent, fontWeight: 900 }}>{formatAllocationNumber(item.orderNumber)}</Typography>
            <Typography variant="body2" sx={{ color: APP_UI.text, fontWeight: 750 }}>{item.plant}</Typography>
            <Typography variant="caption" sx={{ color: APP_UI.textMuted }}>{item.plantArea}</Typography>
            <Typography variant="caption" sx={{ color: APP_UI.textMuted }}>Hersteller: {item.manufacturer || "–"}</Typography>
          </AppCard>
        ))}
        {results.length === 0 && <Typography variant="body2" sx={{ color: APP_UI.textMuted, textAlign: "center", py: 3 }}>Keine Kontierung gefunden.</Typography>}
      </Stack>
    </AppBottomSheet>
  );
}
